// web/src/components/loader/QuipBubbles.tsx
// Quips as speech bubbles that pop up around the edges of the cover. One shows at a time: each
// new quip lands at the next spot (cycling the four corners around the centered cover), TYPES IN
// word-by-word, and holds until the next replaces it (a brief cross-fade as one exits / one
// enters). Each bubble has a large radius except the ONE corner facing the cover, which gets a
// small radius (a speech nib pointing at the cover). Width is tuned so most quips wrap to ~two lines.
//
// Each card is memoized so a new quip arriving never re-triggers the others' type-in.
// A11y/tests: the full quip lives in an `sr-only` node (screen readers + getByText see the whole
// line at once); the animated word-spans are aria-hidden. Reduced motion → words appear instantly.
import { Fragment, memo, useEffect, useRef, useState } from "react";
import type { CSSProperties, JSX } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

type Bubble = { id: number; text: string; spot: number };

// Spots around the (centered) cover. Offsets are from the cover center; `radius` (TL TR BR BL)
// keeps a large radius everywhere except the corner that faces the cover (the small nib).
const SPOTS: { style: CSSProperties; radius: string }[] = [
  { style: { right: "205px", bottom: "120px" }, radius: "26px 26px 8px 26px" }, // upper-left  → nib bottom-right
  { style: { left: "205px", bottom: "120px" }, radius: "26px 26px 26px 8px" },  // upper-right → nib bottom-left
  { style: { right: "215px", top: "110px" }, radius: "26px 8px 26px 26px" },    // lower-left  → nib top-right
  { style: { left: "215px", top: "110px" }, radius: "8px 26px 26px 26px" },     // lower-right → nib top-left
];
const MAX_VISIBLE = 1; // one bubble on screen at a time

// On a narrow (mobile) viewport there's no room beside the cover, so the bubble goes ABOVE /
// BELOW it, centered — cycling those two spots instead of the four desktop corners.
const MOBILE_SPOTS: { style: CSSProperties; radius: string }[] = [
  { style: { left: "-6.25rem", bottom: "150px" }, radius: "26px 26px 8px 26px" }, // above the cover → nib toward cover (bottom)
  { style: { left: "-6.25rem", top: "150px" }, radius: "26px 8px 26px 26px" },    // below the cover → nib toward cover (top)
];

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = (): void => setNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return narrow;
}

const QuipCard = memo(function QuipCard({ text, spot, narrow, reduced }: { text: string; spot: number; narrow: boolean; reduced: boolean }): JSX.Element {
  const set = narrow ? MOBILE_SPOTS : SPOTS;
  const s = set[spot % set.length];
  const words = text.split(" ");
  return (
    <motion.div
      aria-label={text}
      className="absolute w-[12.5rem] bg-[#1b2745] px-5 py-4 text-[0.9rem] font-medium leading-snug text-white"
      style={{ ...s.style, borderRadius: s.radius }}
      initial={{ opacity: 0, scale: 0.82 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    >
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {words.map((w, i) => (
          <Fragment key={i}>
            <motion.span
              className="inline-block"
              initial={reduced ? { opacity: 1 } : { opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={reduced ? { duration: 0 } : { delay: 0.12 + i * 0.075, duration: 0.18, ease: "easeOut" }}
            >
              {w}
            </motion.span>
            {i < words.length - 1 ? " " : ""}
          </Fragment>
        ))}
      </span>
    </motion.div>
  );
});

export function QuipBubbles({ quip }: { quip: string | undefined }): JSX.Element | null {
  const reduced = useReducedMotion();
  const narrow = useIsNarrow();
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const idRef = useRef(0);
  const spotRef = useRef(-1);
  const prevRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!quip || quip === prevRef.current) return;
    prevRef.current = quip;
    spotRef.current += 1; // raw counter; the active spot set (desktop/mobile) is chosen at render
    const next: Bubble = { id: idRef.current++, text: quip, spot: spotRef.current };
    setBubbles((prev) => [...prev, next].slice(-MAX_VISIBLE));
  }, [quip]);

  if (bubbles.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[8]">
      {/* zero-size anchor at the cover center; bubbles offset out to the corners from here */}
      <div className="absolute left-1/2 top-[48%]">
        <AnimatePresence>
          {bubbles.map((b) => (
            <QuipCard key={b.id} text={b.text} spot={b.spot} narrow={narrow} reduced={!!reduced} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
